import * as preact from "preact";

import * as modalController from "modalController";
import * as practiceUtils from "practiceUtils";
import readlangClient from "readlangClient";
import { cn } from "shared/utils";
import stores from "stores/stores";
import Dialog from "views/Dialog";

interface Props {
  vocab: {
    word: string;
    context: string;
    translation: string;
    toSave: boolean;
  }[];
  onToggle: (index: number) => void;
  selectAll: (value: boolean) => void;
  userChatID: string;
  appRouter: any;
}

interface State {
  saving: boolean;
  /** Set once saved, so the same vocab can't be saved twice */
  savedUserWordIDs?: string[];
}

const VocabSavedDialog = (props: {
  count: number;
  onKeepChatting: () => void;
  onPractice: () => void;
}) => (
  <Dialog
    class="vocabSavedDialog"
    heading={`${props.count} ${props.count === 1 ? "word" : "words"} saved`}
    headingIcon="ok"
    actions={
      <>
        <button onClick={props.onKeepChatting}>Keep chatting</button>
        <button class="prominent" onClick={props.onPractice}>
          Practice now
        </button>
      </>
    }
  >
    <p>They're in your word list, ready to practice whenever you like.</p>
  </Dialog>
);

class VocabSelector extends preact.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = { saving: false };
  }

  /** Selection is frozen while saving and after, so "Saved" always describes what was saved */
  selectionLocked() {
    return this.state.saving || this.state.savedUserWordIDs !== undefined;
  }

  toggleWord = (index: number) => {
    if (!this.selectionLocked()) {
      this.props.onToggle(index);
    }
  };

  render() {
    const allSelected = this.props.vocab.every((item) => item.toSave);
    const noneSelected = this.props.vocab.every((item) => !item.toSave);
    const saved = this.state.savedUserWordIDs !== undefined;
    const locked = this.selectionLocked();
    return (
      <div className="vocabSelector">
        <h3>Save vocab?</h3>
        <table className="vocabTable">
          {this.props.vocab.length > 1 ? (
            <tr>
              <td></td>
              <td class="selectAllLabelCell">
                <label for="selectAll">Select all</label>
              </td>
              <td>
                <input
                  id="selectAll"
                  type="checkbox"
                  checked={allSelected}
                  disabled={locked}
                  onChange={() => this.props.selectAll(!allSelected)}
                />
              </td>
            </tr>
          ) : null}
          {this.props.vocab.map((vocabItem, index) => {
            const clozeSentence = practiceUtils.buildClozeSentence(
              vocabItem.context,
              vocabItem.word,
              false
            );

            return (
              <tr
                className={cn({
                  vocabItem: true,
                  selected: vocabItem.toSave,
                })}
              >
                <td
                  class="word"
                  onClick={() => {
                    this.toggleWord(index);
                  }}
                >
                  {clozeSentence.map((part) =>
                    part.type === "target" ? (
                      <strong>{part.text}</strong>
                    ) : (
                      <>{part.text}</>
                    )
                  )}
                </td>
                <td
                  class="translation"
                  onClick={() => {
                    this.toggleWord(index);
                  }}
                >
                  {vocabItem.translation}
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={vocabItem.toSave}
                    disabled={locked}
                    onChange={() => {
                      this.toggleWord(index);
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </table>
        <button
          class="prominent"
          disabled={this.state.saving || noneSelected}
          onClick={() => this.practice()}
        >
          Practice Now
        </button>
        <button
          class={cn({ saved })}
          disabled={locked || noneSelected}
          onClick={() => this.saveForLater()}
        >
          {saved ? (
            <>
              <i class="icon-ok"></i> Saved
            </>
          ) : (
            "Save To Practice Later"
          )}
        </button>
        <p style={{ marginBottom: 0 }}>
          <small>
            This only includes corrections of your own writing. If you
            translated some of Ami's messages, these will have been saved to
            your account already, just like how Readlang works when reading
            other texts.
          </small>
        </p>
      </div>
    );
  }

  async saveVocab() {
    this.setState({ saving: true });

    try {
      // Save to the user's account
      const userWordIDs: string[] = await Promise.all(
        this.props.vocab
          .filter((item) => item.toSave)
          .map(async (vocabItem) => {
            const response = await readlangClient.request(
              "POST",
              "/api/userWord",
              {
                word: vocabItem.word,
                translation: vocabItem.translation,
                language: stores.user.currentLearningLanguage.value,
                contexts: [
                  {
                    text: vocabItem.context,
                    userChatID: this.props.userChatID,
                    source: "chat:user",
                  },
                ],
              }
            );
            return response.data.upserted ?? response.data.updated;
          })
      );

      this.setState({ savedUserWordIDs: userWordIDs });
      return userWordIDs;
    } finally {
      this.setState({ saving: false });
    }
  }

  async saveForLater() {
    const userWordIDs = await this.saveVocab();

    const navigateFromDialog = (url: string) => {
      modalController.cancel();
      this.props.appRouter.navigate(url, { trigger: true });
    };
    modalController.show(
      <VocabSavedDialog
        count={userWordIDs.length}
        onKeepChatting={() => navigateFromDialog("/chat")}
        onPractice={() => navigateFromDialog(this.practiceURL(userWordIDs))}
      />
    );
  }

  async practice() {
    const userWordIDs = this.state.savedUserWordIDs ?? (await this.saveVocab());
    this.props.appRouter.navigate(this.practiceURL(userWordIDs), {
      trigger: true,
    });
  }

  practiceURL(userWordIDs: string[]) {
    return `/practice/words/${userWordIDs.join("|")}?from=chat`;
  }
}

export default VocabSelector;
